import { ApiError, ValidationApiError } from "@anna-money/anna-web-lib/api";
import type { AbnFormValues } from "helpers/forms/acn/abnTypes";
import { type CompanyFormValues } from "helpers/forms/acn/companyTypes";
import type { FlowTypeFormValues } from "helpers/forms/acn/flowTypes";
import { type DirectorStepFormValues } from "helpers/forms/au/directorTypes";
import type { IdentiyFormValues } from "helpers/forms/au/identityTypes";
import type { ConsentFormValues } from "helpers/forms/consentTypes";
import { extractBackendValidationError } from "helpers/forms/errors";
import { loadLibPhoneNumber } from "helpers/phoneLib";
import { getUrlParam } from "helpers/url";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { BillingControllerStore } from "services/billingController/billingControllerStore";
import type { BusinessActivitiesStore } from "services/businessActivities/businessActivitiesStore";
import { type CompanyStore } from "services/company/companyStore";
import { type CompanyAddressStore } from "services/companyAddress/au/companyAddressStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import type { CompanyNameFormValues } from "services/companyNameForm/au/companyNameFormStore";
import { type CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import type { ConsentStore } from "services/consent/consentStore";
import type { CountryStore } from "services/country/countryStore";
import { type DirectorEntity, type DirectorStore } from "services/director/directorStore";
import type { FeatureStore } from "services/feature/featureStore";
import { type IncorporationStore } from "services/incorporation/incorporationStore";
import { type IssuesStore } from "services/issues/issuesStore";
import { type PaymentMethodsStore } from "services/paymentMethods/paymentMethodsStore";
import type { PricingPlanStore } from "services/pricingPlan/au/pricingPlanStore";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { RegisterFormStoreBase } from "services/registerForm/registerFormStoreBase";
import { AbnStepEntity, type AbnStepStore } from "services/stepStore/au/acn/abnStepStore";
import { type CompanyAcnStepEntity, type CompanyAcnStepStore } from "services/stepStore/au/acn/companyAcnStepStore";
import { CompanyNameAcnStepStore, CompanyNameEntity } from "services/stepStore/au/acn/companyNameAcnStepStore";
import { DirectorAcnStepStore } from "services/stepStore/au/acn/directorAcnStepStore";
import { IdentityStepEntity, type IdentityStepStore } from "services/stepStore/au/identityStepStore";
import type { SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";
import { flowTypeUrlParam, parseFlowTypeUrlParam } from "services/user/flowTypeUrlParam";
import type { UserStore } from "services/user/userStore";
import { FlowType } from "services/user/userTypes";
import { FormError } from "types/errors";
import { type FormSubmitResult, FormValidationError } from "types/form";
import { type AcnRegisterFormStepsStore } from "./acnRegisterFormStepsStore";
import { AcnFormStep } from "./constants";

export class AcnRegisterFormStore extends RegisterFormStoreBase<AcnFormStep> {
  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _companyAddressStore: CompanyAddressStore,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _paymentMethodsStore: PaymentMethodsStore,
    private readonly _consentStore: ConsentStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _businessActivitiesStore: BusinessActivitiesStore,
    private readonly _pricingPlanStore: PricingPlanStore,
    protected readonly _userStore: UserStore,
    protected readonly _sumsubApplicationStore: SumsubApplicationStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _registerFormStepsStore: AcnRegisterFormStepsStore,
    protected readonly _incorporationStore: IncorporationStore,
    protected readonly _companyIssuesStore: IssuesStore,
    protected readonly _companyStepStore: CompanyAcnStepStore,
    protected readonly _identityStepStore: IdentityStepStore,
    protected readonly _abnStepStore: AbnStepStore,
    protected readonly _countryStore: CountryStore,
    protected readonly _companyNameAcnStepStore: CompanyNameAcnStepStore,
    protected readonly _directorAcnStepStore: DirectorAcnStepStore,
    private readonly _featureStore: FeatureStore,
    private readonly _billingControllerStore: BillingControllerStore,
  ) {
    super(_registerFormStepsStore, _userStore, _incorporationStore, _companyIssuesStore, _analyticsManager);
  }

  protected async _init(): Promise<void> {
    this._analyticsManager.event("acn-form-init-started");

    await this._companyStore.init();

    await Promise.all([
      this._consentStore.init(),
      this._companyAddressStore.init(),
      this._directorStore.init(),
      this._pricingPlanStore.init(),
      this._companyProductsStore.init(),
      this._sumsubApplicationStore.init(),
      this._companyIssuesStore.init(),
      this._incorporationStore.init(),
      this._paymentMethodsStore.init(),
      this._companyBusinessActivitiesStore.init(),
      this._businessActivitiesStore.init(),
      this._countryStore.init(),
      loadLibPhoneNumber(this._analyticsManager),
    ]);

    if (this._featureStore.hasFeature("formationPaymentStatus")) {
      await this._billingControllerStore.fetchPaymentStatus();

      this._analyticsManager.event("acn-form-payment-status-checked", {
        isPaymentCompleted: this._billingControllerStore.isPaymentCompleted,
      });
    }

    await this._tryApplyFlowTypeFromUrl();

    this._analyticsManager.event("acn-form-init-completed");
  }

  protected _getRelevantStep(): AcnFormStep {
    // Check if flow type is selected
    if (!this._userStore.flowType) {
      return AcnFormStep.FLOW_SELECT;
    }

    // Check consent
    if (!this._consentStore.hasAuConsents) {
      return AcnFormStep.CONSENT;
    }

    // Check if company name exists
    if (!this._companyStore.company?.companyName) {
      return AcnFormStep.COMPANY_NAME;
    }

    // Check if director exists and all director details are complete
    if (!this._directorStore.mainDirector || !this._directorStore.checkAllDirectorDetailExists()) {
      return AcnFormStep.DIRECTOR;
    }

    // Check if shares are properly set
    if (!this._directorStore.checkSharesExist()) {
      return AcnFormStep.DIRECTOR_SHARE;
    }

    // Check if company details exist (virtual office, etc.) and the registered-office
    // address has been saved. The address lives in a separate store, so
    // checkAcnCompanyDetailExists() can pass once the virtual-office choice is made
    // but before the address is entered. Without also checking the address here, a
    // resumed session skips the COMPANY step and the customer can pay with no
    // registered office, which crashes ACN submission creation on the backend.
    if (!this._companyStore.checkAcnCompanyDetailExists() || !this._companyAddressStore.address) {
      return AcnFormStep.COMPANY;
    }

    // Check if all ABN details are complete
    if (!this._abnStepStore.checkAllAbnDetailsExist()) {
      return AcnFormStep.ABN;
    }

    // Check if identity verification is in progress
    if (this._sumsubApplicationStore.sumsubApplication) {
      return AcnFormStep.IDENTITY;
    }

    // If payment already completed, skip CHARGE — go directly to COMPLETE
    if (this._featureStore.hasFeature("formationPaymentStatus") && this._billingControllerStore.isPaymentCompleted) {
      this._analyticsManager.event("acn-form-payment-already-completed-skip-charge");
      return AcnFormStep.COMPLETE;
    }

    // Default to identity step if everything else is complete
    return AcnFormStep.IDENTITY;
  }

  protected async _updateStepData(data: unknown, entity?: unknown): Promise<FormSubmitResult> {
    try {
      switch (this._registerFormStepsStore.activeStep.type) {
        case AcnFormStep.FLOW_SELECT: {
          const { flowType = FlowType.AcnAbn } = data as FlowTypeFormValues;
          await this._applyFlowSelection(flowType);
          break;
        }
        case AcnFormStep.CONSENT:
          await this._consentStore.updateConsents(data as ConsentFormValues);
          break;
        case AcnFormStep.COMPANY_NAME:
          await this._companyNameAcnStepStore.updateEntity(data as CompanyNameFormValues, entity as CompanyNameEntity);
          break;
        case AcnFormStep.COMPANY:
          await this._companyStepStore.updateEntity(data as CompanyFormValues, entity as CompanyAcnStepEntity);
          break;
        case AcnFormStep.IDENTITY:
          await this._identityStepStore.updateEntity(data as IdentiyFormValues, entity as IdentityStepEntity);
          break;
        case AcnFormStep.ABN:
          await this._abnStepStore.updateEntity(data as AbnFormValues, entity as AbnStepEntity);
          break;
        case AcnFormStep.DIRECTOR: {
          await this._directorStore.updateMainDirector(data as DirectorStepFormValues, entity as DirectorEntity);
          break;
        }
        case AcnFormStep.DIRECTOR_SHARE:
          await this._directorAcnStepStore.updateMainDirector(data as DirectorStepFormValues, entity as DirectorEntity);
          break;
        case AcnFormStep.CHARGE: {
          await this._createIncorporation();
          break;
        }
      }
    } catch (error) {
      if (error instanceof ValidationApiError) {
        return extractBackendValidationError(error);
      }
      if (error instanceof ApiError || error instanceof FormError) {
        return new FormValidationError(error.message);
      }
      throw error;
    }
  }

  private async _applyFlowSelection(flowType: FlowType): Promise<void> {
    await this._userStore.updateCurrentFlow(flowType);
    await this._updatePricingPlanFromFlowType(flowType);
  }

  private async _tryApplyFlowTypeFromUrl(): Promise<void> {
    const flowType = parseFlowTypeUrlParam(getUrlParam(flowTypeUrlParam));

    if (!flowType || this._userStore.flowType) {
      return;
    }

    await this._applyFlowSelection(flowType);

    this._analyticsManager.event("flow-type-applied-from-url", { flowType });
  }

  private async _updatePricingPlanFromFlowType(flowType: FlowType): Promise<void> {
    let pricingPlan: PricingPlan;

    if (!this._companyStore.company) {
      await this._companyStore.createCompany();
    }

    switch (flowType) {
      case FlowType.SoleTraderAbn:
        pricingPlan = PricingPlan.SoleTraderLaunch;
        break;
      case FlowType.Abn:
      case FlowType.AcnAbn:
        pricingPlan = PricingPlan.EasyCompany;
        break;
      default:
        this._analyticsManager.event("flow-type-pricing-plan-mapping-error", {
          flowType,
          reason: "unknown_flow_type",
        });
        throw new Error(`Unknown flow type for pricing plan mapping: ${String(flowType)}`);
    }

    try {
      await this._companyStore.updatePricingPlan(pricingPlan);

      this._analyticsManager.event("pricing-plan-updated-from-flow-selection", {
        flowType,
        pricingPlan,
      });
    } catch (error) {
      this._analyticsManager.event("pricing-plan-update-error", {
        flowType,
        pricingPlan,
        error: (error as Error).message,
      });
      throw error;
    }
  }
}
